'use client';

import { useCallback } from 'react';
import useSWR from 'swr';

import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { Iconify } from 'src/components/iconify';
import axiosInstance, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

export type Startsida = 'oversikt' | 'kom-igang';

const NYCKEL = 'startsida';

function ärGiltigStartsida(val: unknown): val is Startsida {
  return val === 'oversikt' || val === 'kom-igang';
}

async function hämtareStartsida(url: string): Promise<Startsida | null> {
  try {
    const { data } = await axiosInstance.get(url);
    return ärGiltigStartsida(data) ? data : null;
  } catch {
    return null;
  }
}

export function useStartsidaVal(orgId: string | undefined) {
  const url = orgId ? endpoints.användare.inställning(orgId, NYCKEL) : null;

  const { data, isLoading, mutate } = useSWR<Startsida | null>(url, hämtareStartsida, {
    revalidateOnFocus: false,
  });

  const spara = useCallback(
    async (val: Startsida) => {
      if (!orgId) return;
      await mutate(val, false);
      await axiosInstance.put(endpoints.användare.inställning(orgId, NYCKEL), val, {
        headers: { 'Content-Type': 'text/plain' },
      });
    },
    [orgId, mutate]
  );

  return { startsida: data ?? null, laddar: isLoading, spara };
}

// ----------------------------------------------------------------------

interface StartsidaVäljareProps {
  orgId: string | undefined;
  aktiv: Startsida;
  onByt: (nästa: Startsida) => void;
}

export function StartsidaVäljare({ orgId, aktiv, onByt }: StartsidaVäljareProps) {
  const { spara } = useStartsidaVal(orgId);

  const hantera = useCallback(
    (_: React.MouseEvent, val: Startsida | null) => {
      if (!val || !orgId) return;
      spara(val);
      onByt(val);
    },
    [orgId, spara, onByt]
  );

  return (
    <Stack spacing={0.5} alignItems="flex-end">
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        Visa som startsida
      </Typography>
      <ToggleButtonGroup
        value={aktiv}
        exclusive
        onChange={hantera}
        size="small"
        sx={{
          height: 34,
          '& .MuiToggleButton-root': {
            gap: 0.75,
            px: 1.5,
            textTransform: 'none',
            fontSize: '0.8125rem',
            borderColor: 'divider',
            '&.Mui-selected': {
              bgcolor: 'background.neutral',
              fontWeight: 600,
            },
          },
        }}
      >
        <ToggleButton value="oversikt">
          <Iconify icon="solar:widget-5-bold-duotone" width={16} />
          Översikt
        </ToggleButton>
        <ToggleButton value="kom-igang">
          <Iconify icon="solar:rocket-2-bold-duotone" width={16} />
          Kom igång
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
}
